import { MouseEvent, useState } from 'react'
import { DocumentNode } from 'graphql'
import moment from 'moment-timezone'
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons'
import { message, Tag } from 'antd'
import {
    PLACEMENT,
    FILE_MAX_LENGTH,
    BORDER,
    ACTIONS,
} from '@Admin/AdminQuotingPage/constants'
import { getEmailForPartDFMReport } from '@Admin/AdminQuotingPage/logic'
import { useSelector } from 'react-redux'

import { isQueryDetailsQueryRefactorOn } from '~/redux-modules/query-refactor/selector'
import { getUserIdentity } from '~/redux-modules/identity'
import Box from '~/core/Box'
import TruncatedText from '~/core/TruncatedText'
import Tooltip from '~/core/Tooltip'
import getColor from '~/lib/getColor'
import logger from '~/lib/logger'
import colors from '~/styles/_colors.css'
import SafeDownloadLink from '~/apps/consumer/components/SafeDownloadLink'
import {
    QuoteDetailsDocument,
    useRemoveQmrAttachmentAdditionalRequirementsMutation,
    useRemoveQuoteAttachmentFilesTabMutation,
    AdminQuoteModelRevisionDfmReportFragment as DFMReportsType,
} from '~/gql/graphql'
import { coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_dfmReports } from '~/redux-modules/query-refactor/__generated__/coreQuoteDetails'

import DeleteDfmReportPopover from './DeleteDfmReport'
import { useGetOboDetails } from './logic'
import UploadSourceTag from './UploadSourceTag'

type RefetchQueryType = { query: DocumentNode; variables: { id: string } }

type PartDFMReportCardPropsType = {
    quoteId: string
    quoteUserId?: number
    qmrId?: string
    refetchQuery: RefetchQueryType[]
    isAdminQuotingPage: boolean
    dfmReport:
        | DFMReportsType
        | coreQuoteDetails_quote_quoteModels_items_selectedQuoteModelRevision_dfmReports
    setCollapse?: Function
    earlierDfmReportsLength?: number
    manualRefetch?: (id: string) => void
}
type PopoverVisibleType = { visible: boolean; id: string | null }

export default function PartDFMReportCard(props: PartDFMReportCardPropsType) {
    const {
        isAdminQuotingPage,
        quoteId,
        quoteUserId,
        qmrId,
        refetchQuery,
        dfmReport,
        setCollapse,
        earlierDfmReportsLength,
        manualRefetch,
    } = props

    const identity = useSelector(getUserIdentity)
    const isQueryRefactorEnabled = useSelector(isQueryDetailsQueryRefactorOn)

    const userLoginId = identity.id
    const isCollaborator = !identity?.isAdmin && userLoginId !== quoteUserId
    const isInOboMode = !isAdminQuotingPage && userLoginId !== quoteUserId
    const { isObo, oboUserId } = useGetOboDetails(quoteUserId || '')
    const [isPopoverVisible, setIsPopoverVisible] =
        useState<PopoverVisibleType>({
            visible: false,
            id: null,
        })
    const [deleteQuoteAttachment] = useRemoveQuoteAttachmentFilesTabMutation()

    const [removeRevisionQuoteAttachment] =
        useRemoveQmrAttachmentAdditionalRequirementsMutation(
            !isQueryRefactorEnabled && !manualRefetch
                ? {
                      refetchQueries: () => [...refetchQuery],
                      awaitRefetchQueries: true,
                  }
                : {},
        )

    const refetchQueryForQuoteDetails = [
        {
            query: QuoteDetailsDocument,
            variables: { id: quoteId },
        },
    ]
    const onClickCancel = (evt: MouseEvent) => {
        evt.stopPropagation()

        return setIsPopoverVisible({ visible: false, id: null })
    }
    const handleRemoveAttachment = async (
        quoteAttachmentRevisionId: string,
        attachmentId: string,
    ) => {
        if (!qmrId || !attachmentId) {
            logger.error('Failed to delete attachment.')
            return null
        }
        if (setCollapse && earlierDfmReportsLength === 1) {
            setCollapse(true)
        }

        setIsPopoverVisible({ visible: false, id: null })
        if (isQueryRefactorEnabled && manualRefetch) {
            return removeRevisionQuoteAttachment({
                variables: {
                    quoteModelRevisionId: qmrId,
                    quoteAttachmentRevisionId,
                    userId: isObo && oboUserId ? oboUserId : null,
                    quoteId,
                },
            })
                .then(() => {
                    manualRefetch && manualRefetch(quoteId)
                    return deleteQuoteAttachment({
                        variables: {
                            quoteId,
                            attachmentId,
                        },
                        awaitRefetchQueries: true,
                    }).then(() => {
                        manualRefetch && manualRefetch(quoteId)
                        return message.success('Attachment removed.')
                    })
                })
                .catch(error => {
                    logger.exception(error)
                    message.error('Failed to delete attachment.')
                })
        } else {
            return removeRevisionQuoteAttachment({
                variables: {
                    quoteModelRevisionId: qmrId,
                    quoteAttachmentRevisionId,
                    userId: isObo && oboUserId ? oboUserId : null,
                    quoteId,
                },
            })
                .then(() => {
                    return deleteQuoteAttachment({
                        variables: {
                            quoteId,
                            attachmentId,
                        },
                        refetchQueries: () => [...refetchQueryForQuoteDetails],
                        awaitRefetchQueries: true,
                    }).then(() => message.success('Attachment removed.'))
                })
                .catch(error => {
                    logger.exception(error)
                    message.error('Failed to delete attachment.')
                })
        }
    }

    const handleDeletePopOverVisibleChange = (visible: boolean) => {
        setIsPopoverVisible({ visible, id: null })
    }

    const dfmReportSource =
        'source' in dfmReport ? dfmReport?.source : undefined
    const dfmReportUploaderEmail =
        'uploaderEmail' in dfmReport ? dfmReport?.uploaderEmail : undefined

    return (
        <Box
            direction="col"
            marginBottom="16px"
            borderRadius="4px"
            border={BORDER}
            backgroundColor={colors.white}
            data-test-target={`${PLACEMENT}-panel`}
        >
            <Box
                key={dfmReport?.asset?.createdAt}
                margin="16px 16px 0px 16px"
                direction="row"
            >
                <Box
                    width="85%"
                    color="blackAlpha65"
                    style={{
                        fontSize: '14px',
                        fontWeight: 400,
                    }}
                >
                    <TruncatedText
                        tooltipPlacement="topLeft"
                        maxLength={FILE_MAX_LENGTH}
                    >
                        {dfmReport?.asset?.fileName}
                    </TruncatedText>
                </Box>
                <Box
                    key={ACTIONS}
                    style={{
                        marginRight: '0px',
                        marginLeft: 'auto',
                    }}
                >
                    <SafeDownloadLink
                        name="download-attachment"
                        placement={PLACEMENT}
                        downloadUrl={dfmReport?.asset?.downloadUrl}
                        fileName={dfmReport?.asset?.fileName}
                    >
                        <Tooltip title="Download">
                            <DownloadOutlined
                                style={{
                                    color: getColor('info'),
                                    fontSize: '18px',
                                }}
                            />{' '}
                        </Tooltip>
                    </SafeDownloadLink>
                    {((!isAdminQuotingPage || isInOboMode) &&
                        dfmReportSource === 'Fictiv') ||
                    isCollaborator ? null : (
                        <DeleteDfmReportPopover
                            onClickCancel={onClickCancel}
                            onClickConfirm={() =>
                                handleRemoveAttachment(
                                    dfmReport?.quoteAttachmentRevisionId,
                                    dfmReport?.asset?.id,
                                )
                            }
                            visible={
                                isPopoverVisible.visible &&
                                dfmReport?.asset?.id === isPopoverVisible.id
                            }
                            onVisibleChange={handleDeletePopOverVisibleChange}
                        >
                            <Tooltip title="Delete">
                                <DeleteOutlined
                                    style={{
                                        color: getColor('error'),
                                        fontSize: '18px',
                                        marginLeft: '8px',
                                    }}
                                    onClick={(evt: MouseEvent) => {
                                        evt.stopPropagation()
                                        setIsPopoverVisible({
                                            visible: true,
                                            id: dfmReport?.asset?.id,
                                        })
                                    }}
                                />
                            </Tooltip>
                        </DeleteDfmReportPopover>
                    )}
                </Box>
            </Box>
            <Box margin="0px 16px 4px 16px">
                {dfmReportSource && (
                    <UploadSourceTag
                        dfmReport={dfmReport as DFMReportsType}
                        isAdminQuotingPage={isAdminQuotingPage}
                    />
                )}
                {!dfmReport?.isPublished && (
                    <Tag
                        color="orange"
                        style={{
                            fontSize: '12px',
                            fontWeight: 400,
                        }}
                    >
                        Unreleased DFM
                    </Tag>
                )}
            </Box>
            <Box
                margin="0px 16px 4px 16px"
                color="blackAlpha45"
                style={{ fontSize: '11px', fontWeight: 400 }}
            >
                {dfmReportUploaderEmail && (
                    <TruncatedText
                        tooltipPlacement="topLeft"
                        style={{
                            marginRight: '2px',
                            maxWidth: '224px',
                            width: 'auto',
                        }}
                        maxLength={FILE_MAX_LENGTH}
                    >
                        {getEmailForPartDFMReport(
                            isAdminQuotingPage,
                            dfmReportSource,
                            dfmReportUploaderEmail,
                        )}
                    </TruncatedText>
                )}
                {moment(dfmReport?.asset?.createdAt).format(
                    'MM/DD/YYYY h:mm a',
                )}
            </Box>
        </Box>
    )
}
